import React, { useCallback, useEffect } from "react";

import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography
} from "@mui/material";
import { useModals } from "hooks";
import { ReportGeneratorTemplateListItem } from "store/types";

interface LoadTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  templates: ReportGeneratorTemplateListItem[];
  loading: boolean;
  loadingTemplate: boolean;
  onFetchTemplates: () => Promise<void>;
  onLoadTemplate: (id: number) => Promise<void>;
  onDeleteTemplate: (id: number) => Promise<void>;
  currentTemplateId?: number | null;
}

export const LoadTemplateDialog: React.FC<LoadTemplateDialogProps> = ({
  open,
  onClose,
  templates,
  loading,
  loadingTemplate,
  onFetchTemplates,
  onLoadTemplate,
  onDeleteTemplate,
  currentTemplateId
}) => {
  const { deleteModal } = useModals();

  useEffect(() => {
    if (open) {
      onFetchTemplates();
    }
  }, [open, onFetchTemplates]);

  const handleLoad = async (id: number) => {
    await onLoadTemplate(id);
    onClose();
  };

  const handleDelete = useCallback(
    (e: React.MouseEvent, id: number, templateName: string) => {
      e.stopPropagation();
      deleteModal({
        element: templateName,
        message: "Are you sure you want to delete this template?",
        action: () => onDeleteTemplate(id)
      });
    },
    [deleteModal, onDeleteTemplate]
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: "8px" } } }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Load Template
        <IconButton onClick={onClose} size="sm">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : templates.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <FolderOpenIcon sx={{ fontSize: 48, color: "#94a3b8", mb: 1 }} />
            <Typography color="textSecondary">No saved templates yet</Typography>
            <Typography variant="caption" color="textSecondary">
              Save your first template to see it here
            </Typography>
          </Box>
        ) : (
          <List sx={{ pt: 0 }}>
            {templates.map((template) => (
              <ListItem
                key={template.id}
                disablePadding
                secondaryAction={
                  <IconButton edge="end" onClick={(e) => handleDelete(e, template.id, template.name)} size="sm">
                    <DeleteIcon fontSize="sm" />
                  </IconButton>
                }
                sx={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  mb: 1
                }}
              >
                <ListItemButton
                  onClick={() => handleLoad(template.id)}
                  disabled={loadingTemplate}
                  sx={{
                    borderRadius: "8px",
                    bgcolor: currentTemplateId === template.id ? "#eff6ff" : "transparent",
                    "&:hover": { bgcolor: "#dae0e2" }
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography fontWeight={500}>{template.name}</Typography>
                        {currentTemplateId === template.id && (
                          <Typography
                            variant="caption"
                            sx={{
                              bgcolor: "#0B4549",
                              color: "white",
                              px: 1,
                              py: 0.25,
                              borderRadius: "4px",
                              fontSize: "0.65rem"
                            }}
                          >
                            Current
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box component="span">
                        {template.description && (
                          <Typography variant="body2" color="textSecondary" component="span" display="block">
                            {template.description}
                          </Typography>
                        )}
                        <Typography variant="caption" color="textSecondary" component="span">
                          Created: {formatDate(template.created_at)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
        {loadingTemplate && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255, 255, 255, 0.8)",
              zIndex: 1
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress size={32} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Loading template...
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
